import { defaultParameterizeRoute } from '@spotify-confidence/csr-recorder';
import {
  type Value as ConfidenceValue,
  type Context,
  Value,
} from '@spotify-confidence/sdk';
import {
  type InitSessionRecorderOptions,
  type SessionRecorder,
  initSessionRecorder,
} from '@spotify-confidence/session-recording';

import { BUILD_COMMIT_HASH, IS_E2E_TEST, IS_PREVIEW } from './environment';

type RecorderContextValue =
  | string
  | number
  | boolean
  | RecorderContextValue[]
  | { [key: string]: RecorderContextValue };

type RecorderContext = Record<string, RecorderContextValue>;

export type InitAppSessionRecorderOptions = Pick<
  InitSessionRecorderOptions,
  'clientSecret' | 'parameterizeRoute'
> & {
  context: Context;
};

let appSessionRecorder: SessionRecorder | undefined;

function normalizeContextValue(value: Value.Struct): RecorderContext;
function normalizeContextValue(
  value: ConfidenceValue,
): RecorderContextValue | undefined;
function normalizeContextValue(
  value: ConfidenceValue,
): RecorderContextValue | undefined {
  if (value === undefined || typeof value !== 'object') return value;
  if (Value.isList(value)) return [...value];

  return Object.fromEntries(
    Object.entries(value).flatMap(
      ([key, contextValue]): [string, RecorderContextValue][] => {
        const normalizedValue = normalizeContextValue(contextValue);
        return normalizedValue === undefined ? [] : [[key, normalizedValue]];
      },
    ),
  );
}

export const initAppSessionRecorder = ({
  clientSecret,
  context,
  parameterizeRoute = defaultParameterizeRoute,
}: InitAppSessionRecorderOptions): SessionRecorder | undefined => {
  if (process.env.NODE_ENV !== 'production' || IS_E2E_TEST || IS_PREVIEW)
    return undefined;

  if (appSessionRecorder) return appSessionRecorder;

  appSessionRecorder = initSessionRecorder({
    clientSecret,
    context: {
      ...normalizeContextValue(context),
      origin: window.location.origin,
      path: window.location.pathname,
    },
    appVersion: BUILD_COMMIT_HASH,
    blockSelectors: ['[data-csr-block]', 'button[aria-label*="profile menu"]'],
    maskSelectors: ['[data-csr-mask]'],
    captureConsoleLogs: { levels: ['warn', 'error'] },
    captureNetworkRequests: true,
    userTriggeredOnInput: true,
    parameterizeRoute,
  });

  return appSessionRecorder;
};

export type { SessionRecorder };
